import React, { useEffect, useRef, useState } from 'react';
import {
  AlteracaoDaProposta,
  fetchHistoricoDaProposta,
  VersaoDaProposta,
} from '../../../api/portalCotacoes/portalCotacoesApi';
import '../../../components/modal/add-car/AddCarModal.css';
import './FornecedorDetalhesModal.css';
import '../../../shared/ui/modal-close-button.css';
import '../../../shared/feedback/erro-de-tela.css';

/**
 * O que o fornecedor mudou em cada correção da proposta (portal-cotacoes/at-17).
 *
 * Pedido de Marlon e Larissa no teste de Compras de 29/10/2026: o comprador via "correção 2" sem
 * saber o que mudou. A comparação vem pronta do servidor; aqui só se mostra.
 *
 * 🔴 Falha de carga nunca vira "sem correções" (at-60): o comprador concluiria que o preço que vê
 * é o primeiro que o fornecedor mandou.
 */

interface HistoricoCorrecoesModalProps {
  conviteId: string;
  onFechar: () => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

function valor(campo: string, v: AlteracaoDaProposta['antes']): string {
  if (v === null || v === '') return 'vazio';
  if (typeof v === 'boolean') return v ? 'sim' : 'não';
  if (typeof v === 'number' && (campo === 'Preço unitário' || campo === 'Frete')) {
    return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  }
  return String(v);
}

function dataHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString('pt-BR')} às ${d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

const HistoricoCorrecoesModal: React.FC<HistoricoCorrecoesModalProps> = ({ conviteId, onFechar, sidebarRef }) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [fornecedorNome, setFornecedorNome] = useState('');
  const [versoes, setVersoes] = useState<VersaoDaProposta[]>([]);

  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const r = await fetchHistoricoDaProposta(conviteId);
        if (!ativo) return;
        setFornecedorNome(r.fornecedorNome);
        setVersoes(r.versoes);
      } catch (error) {
        if (ativo) setErro((error as Error).message);
      } finally {
        if (ativo) setCarregando(false);
      }
    })();
    return () => {
      ativo = false;
    };
  }, [conviteId]);

  useEffect(() => {
    const fora = (e: MouseEvent) => {
      const alvo = e.target as Node;
      if (modalRef.current?.contains(alvo) || sidebarRef?.current?.contains(alvo)) return;
      onFechar();
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [onFechar, sidebarRef]);

  return (
    <div className="add-car-modal-overlay">
      <div className="fornecedor-modal-content" ref={modalRef}>
        <div className="fornecedor-modal-header">
          <div className="fornecedor-modal-header-info">
            <h2>Correções da proposta</h2>
            <span className="fornecedor-modal-subtitulo">{fornecedorNome}</span>
          </div>
          <button className="close-button" onClick={onFechar} aria-label="Fechar modal">
            &times;
          </button>
        </div>

        <div className="fornecedor-modal-body">
          {carregando ? (
            <p>Carregando...</p>
          ) : erro ? (
            <p className="erro-de-tela">
              Não foi possível carregar o histórico desta proposta: {erro} A proposta pode ter correções que não estão
              aparecendo aqui.
            </p>
          ) : (
            versoes.map((v) => (
              <div key={v.id} className="fornecedor-modal-secao">
                <h3>
                  {v.versao === 1 ? 'Primeiro envio' : `Correção ${v.versao}`} · {dataHora(v.enviadaEm)}
                  {v.status === 'ATIVA' ? ' · a que vale' : ''}
                </h3>
                {v.versao === 1 ? (
                  <p>Primeira proposta enviada pelo fornecedor.</p>
                ) : v.alteracoes.length === 0 ? (
                  <p>O fornecedor reenviou a proposta sem mudar nenhum valor.</p>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {v.alteracoes.map((a, i) => (
                      <li key={i}>
                        {a.item ? <strong>{a.item}</strong> : <strong>Proposta</strong>}: {a.campo.toLowerCase()} de{' '}
                        {valor(a.campo, a.antes)} para {valor(a.campo, a.depois)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default HistoricoCorrecoesModal;
